import { Navigate, useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaLogo } from "@/shared/components/ColocaLogo";
import { ColocaReveal } from "@/shared/components/motion/ColocaReveal";
import useTranslations from "@/shared/lang/useTranslations";
import { useTheme } from "@/shared/theme/useTheme";

import { LangDropdown } from "../components/LangDropdown";
import { ThemeSwitch } from "../components/ThemeSwitch";
import { VerifyEmailAddress } from "../components/VerifyEmailAddress";
import { VerifyEmailForm } from "../components/VerifyEmailForm";
import { useAuth } from "../hooks/useAuth";

export default function VerifyEmailPage() {
  const { theme, toggleTheme } = useTheme();
  const { t, activeLanguage, setLanguage } = useTranslations("register");
  const navigate = useNavigate();
  const { user, doLogout } = useAuth();

  // Safety nets — the UnverifiedOnlyRoute guard upstream covers these, but we
  // double-check inside the page so refreshes or direct nav can't render an
  // inconsistent state.
  if (!user) return <Navigate to="/auth/register" replace />;
  if (user.email_verified_at) return <Navigate to="/app" replace />;

  // Only for someone who wants a different account altogether: a mistyped address is corrected in place
  // (`VerifyEmailAddress`), keeping the session and the signup data.
  const handleSignOut = () => {
    doLogout();
    navigate("/auth/login", { replace: true });
  };

  return (
    <div className="w-full min-h-screen bg-muted px-4 pb-4">
      <div className="flex flex-col-reverse md:flex md:flex-row justify-end items-center p-4 gap-6">
        <div className="flex items-center justify-end gap-2">
          <LangDropdown
            isWhite={theme === "dark"}
            defaultSelected={activeLanguage}
            items={[
              { label: "EN", value: "en" },
              { label: "ES", value: "es" },
              { label: "PT", value: "pt" },
            ]}
            onChange={(value) => setLanguage(value)}
          />
          <ThemeSwitch theme={theme} white={theme === "dark"} toggleTheme={toggleTheme} />
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center py-8 overflow-y-auto max-w-[480px] m-auto">
        <div className="w-full mb-6 text-center">
          <ColocaLogo variant="black" className="m-auto max-w-60 mb-6" />
          <h1 className="text-3xl font-semibold">{t("verify_title")}</h1>
          <p className="text-muted-foreground mt-1">{t("verify_subtitle")}</p>
          <VerifyEmailAddress email={user.email} />
        </div>

        {/* Keyed by the address too: a corrected email means a new code, so the form starts over — empty
            field, fresh entrance — instead of keeping digits typed for the old one. */}
        <ColocaReveal key={`${activeLanguage}:${user.email}`} className="flex w-full justify-center">
          <div data-reveal className="w-full max-w-md">
            <VerifyEmailForm email={user.email} />
          </div>
        </ColocaReveal>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t("verify_sign_out_prompt")}{" "}
          <ColocaButton variant="link" size="sm" className="h-auto p-0" onClick={handleSignOut}>
            {t("verify_sign_out")}
          </ColocaButton>
        </p>
      </div>
    </div>
  );
}
